import { useLingui } from "@lingui/react/macro";
import { ApiError } from "@pontimed/api-client";

/**
 * Machine-readable code of a failed API call.
 *
 * Most routes answer `{ error: "<human message>", code: "<CODE>" }`, while
 * older shared gates put the code straight in `error` (DPA clickwrap
 * `dpa_acceptance_required`, 2FA `TOTP_REQUIRED`). Read `code` first, then
 * fall back to `error`.
 */
export function apiErrorCode(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body as { code?: unknown; error?: unknown } | null;
  if (typeof body?.code === "string") return body.code;
  if (typeof body?.error === "string") return body.error;
  return null;
}

/**
 * The record is no longer in the state the action expected (answered,
 * cancelled, expired or closed) by the time it reached the API. Older API
 * builds answered 400, newer ones 409 — accept both.
 */
export function isBadStatusError(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    (err.status === 400 || err.status === 409) &&
    apiErrorCode(err) === "BAD_STATUS"
  );
}

/**
 * Translated message for a failed API call (P17). Maps the codes the API
 * actually sends, then the HTTP status, then the caller's fallback. Never
 * returns the API's raw `error` string — those are English, written for
 * developers.
 *
 *   const errorMessage = useApiErrorMessage();
 *   showToast(errorMessage(err, t`Could not accept the appointment`), "error");
 */
export function useApiErrorMessage() {
  const { t } = useLingui();
  return (err: unknown, fallback: string): string => {
    switch (apiErrorCode(err)) {
      case "BAD_STATUS":
        return t`This was already handled. The list has been refreshed.`;
      case "SLOT_TAKEN":
        return t`That time slot is no longer available. Pick another one.`;
      case "TOO_EARLY":
        return t`The appointment hasn't started yet.`;
      case "TOO_LATE":
        return t`It's too late for this action.`;
      case "PAID_MEANWHILE":
        return t`The payment went through in the meantime.`;
      case "PAID_ONLINE":
        return t`This was paid online, so the payment status follows the card payment.`;
      case "PRICE_CHANGED":
        return t`The price changed. Review it and try again.`;
      case "BOOKING_QUOTA_EXHAUSTED":
        return t`The monthly booking limit for this plan has been reached.`;
      case "RATE_LIMITED":
        return t`Too many attempts. Wait a moment and try again.`;
      case "TOKEN_EXPIRED":
      case "INVALID_TOKEN":
        return t`This link has expired or is no longer valid.`;
      default:
        break;
    }
    if (err instanceof ApiError) {
      if (err.status === 401) return t`Your session has expired. Sign in again.`;
      if (err.status === 403) return t`You don't have access to this.`;
      if (err.status === 404) return t`This item no longer exists.`;
      if (err.status === 429) return t`Too many attempts. Wait a moment and try again.`;
      if (err.status === 0 || err.status >= 500) {
        return t`We couldn't reach the server. Check your connection and try again.`;
      }
    } else if (err instanceof TypeError) {
      // fetch() network failure (offline, DNS, CORS)
      return t`We couldn't reach the server. Check your connection and try again.`;
    }
    return fallback;
  };
}
